import { Loading, useDataQuery } from "@lyfegen-platform/react-common";
import { sortBySelectors } from "@lyfegen-platform/utils";
import { Box, MenuItem, TextField, Typography } from "@material-ui/core";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ViewportList } from "react-viewport-list";
import FlexTypographyComponent from "../../../../components/FlexTypographyComponent";
import LyfeAgreementTag from "../../../../components/LyfeAgreementTag";
import { useDataMutation } from "../../../../hooks/useDataModule";
import { SelectableValue } from "../../../../models/common/selectable-value";
import { roles, userRoleDescriptions, userRoleLabels } from "../../../../models/common/user-roles";
import { UserRoleRequestUpdateInput } from "../../../../types/graphql-global-types";
import startUpper from "../../../../utils/start-upper";
import { UPDATE_AGREEMENT_USER_ROLES } from "../../../agreements/drawers/queries";
import { GET_ALL_AGREEMENTS_SPECIFIC_ROLES_QUERY } from "../queries";
import { GetAllAgreementsSpecificRolesQuery } from "../types/queries.generated";
import useOrganizationStyles from "../use-organization-styles";

interface AgreementSpecificRolesActionProps {
	orgId: string;
	selectedUsers: Array<SelectableValue>;
	selectedTags: Array<SelectableValue>;
}

const AgreementSpecificRolesAction: React.FC<AgreementSpecificRolesActionProps> = ({ orgId, selectedUsers, selectedTags }) => {
	const { t } = useTranslation();
	const { menuItemContent, optionDescription, agreementsTable, agreementSpecificRolesContent } = useOrganizationStyles();
	const [agreements, setAgreements] = useState<GetAllAgreementsSpecificRolesQuery["GetAllAgreementsSpecificRoles"]>([]);
	const [filteredData, setFilteredData] = useState<GetAllAgreementsSpecificRolesQuery["GetAllAgreementsSpecificRoles"]>([]);
	const ref = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(0);

	const handleResize = useCallback(() => {
		const newHeight = document.documentElement.clientHeight - (ref.current?.offsetTop || 0) - 200;
		setHeight(newHeight);
	}, []);

	useEffect(() => {
		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const getNewFilteredData = (data: GetAllAgreementsSpecificRolesQuery["GetAllAgreementsSpecificRoles"]) => {
		const users = selectedUsers.map((user) => user.label);
		const tags = selectedTags.map((tag) => tag.value);

		return data.filter(
			(agreement) =>
				(!users.length || users.includes(getUserName(agreement))) && (!tags.length || tags.includes(agreement.agreementTags[0]))
		);
	};

	const handleComplete = (tempData: GetAllAgreementsSpecificRolesQuery) => {
		const data = (tempData?.GetAllAgreementsSpecificRoles || []).slice().sort(
			sortBySelectors(
				(element: any) => element.lastName,
				(elem: any) => elem.firstName
			)
		);
		setAgreements(data);
		setFilteredData(getNewFilteredData(data));
	};

	const getUserName = (agreement: GetAllAgreementsSpecificRolesQuery["GetAllAgreementsSpecificRoles"][0]) =>
		`${agreement.lastName} ${agreement.firstName}`;

	const { loading, error, refetch } = useDataQuery<GetAllAgreementsSpecificRolesQuery>(GET_ALL_AGREEMENTS_SPECIFIC_ROLES_QUERY, {
		variables: { input: { id: orgId, users: [], tags: [], domains: [] } },
		fetchPolicy: "no-cache",
		onCompleted: (tempData) => handleComplete(tempData),
	});
	const mutationsVariable = {
		onCompleted: () => {
			refetch();
		},
		refetchQueries: [
			{
				query: GET_ALL_AGREEMENTS_SPECIFIC_ROLES_QUERY,
				variables: {
					input: { id: orgId, users: [], tags: [], domains: [] },
				},
				onCompleted: (tempData: any) => handleComplete(tempData),
			},
		],
	};
	const [mutate] = useDataMutation(UPDATE_AGREEMENT_USER_ROLES, mutationsVariable);

	useEffect(() => {
		setFilteredData(getNewFilteredData(agreements));
	}, [selectedUsers, selectedTags]);

	function handleUserChanges(
		e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
		agreement: GetAllAgreementsSpecificRolesQuery["GetAllAgreementsSpecificRoles"][0]
	) {
		if (!e.target.value || !agreement.userId) return;

		mutate({
			variables: {
				input: {
					agreementId: agreement.agreementId,
					agreementName: agreement.agreementName,
					userId: agreement.userId,
					role: e.target.value,
				} as UserRoleRequestUpdateInput,
			},
		});
	}

	if (error) return <Typography color="error">Error...{console.error(error)}</Typography>;

	return (
		<Fragment>
			<div hidden={loading} className={`${agreementSpecificRolesContent} scroll-container`} ref={ref} style={{ height }}>
				<table className={agreementsTable}>
					<colgroup>
						<col width="14%" />
						<col width="14%" />
						<col width="14%" />
						<col width="14%" />
						<col width="16%" />
						<col width="14%" />
						<col width="14%" />
					</colgroup>
					<thead>
						<tr>
							<th>{startUpper(t("username"))}</th>
							<th>{startUpper(t("agreement-tag"))}</th>
							<th>{startUpper(t("agreement-name"))}</th>
							<th>{startUpper(t("agreement-id"))}</th>
							<th>{startUpper(t("specific-agreement-role"))}</th>
							<th></th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{(!filteredData || !filteredData.length) && (
							<tr>
								<td style={{ textAlign: "center", height: "200px" }} rowSpan={3} colSpan={7}>
									{t("no")} {t("agreements")} {t("to-display")}
								</td>
							</tr>
						)}
						{!!filteredData.length && (
							<ViewportList viewportRef={ref} items={filteredData}>
								{(item) => (
									<tr key={item.agreementId} className="item">
										<td>
											<Box display="flex" alignItems="center" mr={3}>
												<Typography variant="body2">
													{item.lastName} {item.firstName}
												</Typography>
											</Box>
										</td>
										<td>
											<Box display="flex" alignItems="center" mr={3}>
												{item.agreementTags.map((tag: any, index: number) => (
													<Box key={index} display="flex" alignItems="center" mr={3}>
														<LyfeAgreementTag key={index} hideTooltip title={tag} height={20} />
													</Box>
												))}
											</Box>
										</td>
										<td>
											<Box display="flex" maxWidth={"240px"} alignItems="center" mr={3}>
												<FlexTypographyComponent
													containerStyles={{ alignItems: "center" }}
													text={item.agreementName}
													variant="body2"
												/>
											</Box>
										</td>
										<td>
											<Box display="flex" alignItems="center" mr={3}>
												<Typography variant="body2">{item.agreementId}</Typography>
											</Box>
										</td>
										<td>
											<TextField
												variant="standard"
												select
												value={item.agreementUserRole}
												onChange={(e) => handleUserChanges(e, item)}
												style={{ width: "100%" }}
												SelectProps={{
													MenuProps: {
														PaperProps: {
															style: {
																borderTop: "solid 2px #367BF5",
																borderRadius: "5px",
															},
														},
													},
													native: false,
													renderValue: (v: any) => (
														<Typography variant="body2">{t(userRoleLabels[v])}</Typography>
													),
												}}
												InputProps={{ disableUnderline: true }}
											>
												{roles.map((o, ii) => (
													<MenuItem key={ii} value={o.value}>
														<Box className={menuItemContent}>
															<Typography variant="body2">{t(userRoleLabels[o.value])}</Typography>
															<Typography variant="body2" className={optionDescription}>
																{t(userRoleDescriptions[o.value])}
															</Typography>
														</Box>
													</MenuItem>
												))}
											</TextField>
										</td>
										<td colSpan={2}></td>
									</tr>
								)}
							</ViewportList>
						)}
					</tbody>
				</table>
			</div>
			{loading && (
				<Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
					{" "}
					<Loading />
				</Box>
			)}
		</Fragment>
	);
};

export default AgreementSpecificRolesAction;
